import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useAddCustomerGroupMember, useCustomerGroupMembers, useRemoveCustomerGroupMember } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import type { CustomerGroupMemberRequest } from '../../model/customerGroupTypes';

/** Curated presets for the free-string `customerIdentityKind` (the BFF contract has no enum for
 * it — "e.g. TELEGRAM, KEYCLOAK, CUSTOMER"). Not exhaustive; a served member whose kind isn't in
 * this list still renders (its raw string, never through a label lookup). */
const IDENTITY_KIND_PRESETS = ['CUSTOMER', 'TELEGRAM', 'KEYCLOAK', 'MAX'];

export interface CustomerGroupMembersDrawerProps {
  open: boolean;
  group: { code: string; name: string } | null;
  /** STATIC members management needs `catalog.pricing.manage`. */
  canManage: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * STATIC group members drawer (design spec §8.10) — a mono customer-id input plus a kind preset
 * `Select`, mirroring the story-006 mono-id-input precedent for low-frequency reference data. The
 * kind selector is an addition the design spec's own mock did not show (it only sketched the id
 * input) — `CustomerGroupMemberRequest.customerIdentityKind` is schema-required, so it cannot be
 * omitted; this is the smallest control that satisfies the live contract.
 */
export function CustomerGroupMembersDrawer({
  open,
  group,
  canManage,
  onClose,
  onResult,
}: CustomerGroupMembersDrawerProps) {
  const { t } = useTranslation('catalog');
  const [kind, setKind] = useState('CUSTOMER');
  const [id, setId] = useState('');

  const membersQuery = useCustomerGroupMembers(group?.code, { enabled: open && Boolean(group) });
  const addMutation = useAddCustomerGroupMember();
  const removeMutation = useRemoveCustomerGroupMember();

  const handleAdd = () => {
    if (!group || !id.trim()) return;
    addMutation.mutate(
      { code: group.code, body: { customerIdentityKind: kind, customerIdentityId: id.trim() } },
      {
        onSuccess: () => {
          setId('');
          onResult('success', t('customerGroupsPanel.members.added'));
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  const handleRemove = (member: CustomerGroupMemberRequest) => {
    if (!group) return;
    removeMutation.mutate(
      { code: group.code, member },
      {
        onSuccess: () => onResult('success', t('customerGroupsPanel.members.removed')),
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  const members = membersQuery.data ?? [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 }, display: 'flex', flexDirection: 'column' } } }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {t('customerGroupsPanel.members.title', { name: group?.name ?? '' })}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        <Stack spacing={2}>
          {canManage ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
              <TextField
                select
                size="small"
                label={t('customerGroupsPanel.members.kindLabel')}
                value={kind}
                onChange={(event) => setKind(event.target.value)}
                sx={{ minWidth: 130 }}
              >
                {IDENTITY_KIND_PRESETS.map((preset) => (
                  <MenuItem key={preset} value={preset}>
                    {preset}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={t('customerGroupsPanel.members.idLabel')}
                value={id}
                onChange={(event) => setId(event.target.value)}
                fullWidth
                slotProps={{ htmlInput: { style: { fontFamily: '"JetBrains Mono", monospace' } } }}
              />
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={!id.trim() || addMutation.isPending}
                startIcon={addMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <AddRoundedIcon />}
                sx={{ flexShrink: 0 }}
              >
                {t('customerGroupsPanel.members.add')}
              </Button>
            </Stack>
          ) : null}

          <Divider />

          {membersQuery.isLoading ? (
            <LoadingState label={t('customerGroupsPanel.members.loading')} fullHeight={false} />
          ) : membersQuery.isError ? (
            <ErrorState message={t('customerGroupsPanel.members.loadError')} onRetry={() => void membersQuery.refetch()} />
          ) : members.length === 0 ? (
            <EmptyState title={t('customerGroupsPanel.members.empty')} />
          ) : (
            <Stack spacing={1}>
              {members.map((member) => {
                const busy =
                  removeMutation.isPending &&
                  removeMutation.variables?.member.customerIdentityId === member.customerIdentityId &&
                  removeMutation.variables?.member.customerIdentityKind === member.customerIdentityKind;
                return (
                  <Stack
                    key={`${member.customerIdentityKind}:${member.customerIdentityId}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ py: 0.5 }}
                  >
                    <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
                      {member.customerIdentityKind}: {member.customerIdentityId}
                    </Typography>
                    {canManage ? (
                      <IconButton
                        size="small"
                        aria-label={t('customerGroupsPanel.members.removeAria', { id: member.customerIdentityId })}
                        disabled={busy}
                        onClick={() => handleRemove(member)}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}
